import { useState } from "react";
import { Link } from "react-router";
import { ArrowLeft, CheckCircle2, KeyRound } from "lucide-react";

import { AuthLayout } from "@/components/auth/AuthLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useForgotPassword } from "@/hooks/useAuth";
import { getAuthErrorMessage } from "@/lib/auth";

type Paso = "correo" | "enviado";

/**
 * Recuperación de contraseña, paso 1: pedir el enlace (`POST /api/forgot-password`).
 * El paso 2 —definir la contraseña con el token del correo— vive en
 * `ResetPassword.tsx`.
 *
 * No hay selector de correo enmascarado: el login es por email (identificador
 * único), así que no existe la ambigüedad RUT → varios correos.
 *
 * **La confirmación NO afirma que el correo salió, y no puede.** La API
 * responde SIEMPRE 200 con el mismo mensaje —exista o no la cuenta— para que
 * nadie enumere usuarios desde acá, así que lo único que esta pantalla puede
 * decir con verdad es "si tu correo está registrado, te enviamos un enlace".
 *
 * Lo que sí se muestra es un fallo REAL: throttle, red caída, 5xx. Esa es la
 * diferencia con la versión anterior, que era un prototipo —un `setTimeout` de
 * 900 ms y directo a "Revisa tu correo"— y por lo tanto mostraba la misma
 * pantalla de éxito cuando no se había pedido nada.
 */
export function RecuperarContrasena() {
  const [paso, setPaso] = useState<Paso>("correo");
  const [email, setEmail] = useState("");
  const forgotPassword = useForgotPassword();

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    forgotPassword.mutate({ email }, { onSuccess: () => setPaso("enviado") });
  }

  function reiniciar() {
    setEmail("");
    // El `reset()` importa: sin él, el error del intento anterior sigue
    // dibujado al volver al formulario, señalando un fallo que ya no
    // corresponde al correo que la persona está por escribir.
    forgotPassword.reset();
    setPaso("correo");
  }

  return (
    <AuthLayout>
      {paso === "correo" ? (
        <>
          <div className="mb-8">
            <div className="bg-accent text-accent-foreground mb-5 flex h-12 w-12 items-center justify-center rounded-2xl">
              <KeyRound className="h-6 w-6" />
            </div>
            <h1 className="text-3xl font-extrabold tracking-tight">
              Recuperar contraseña
            </h1>
            <p className="text-muted-foreground text-caption mt-2">
              Ingresa tu correo y te enviaremos un enlace para restablecer tu
              contraseña.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email">Correo electrónico</Label>
              <Input
                id="email"
                type="email"
                required
                autoComplete="email"
                placeholder="tu.correo@consultek.cl"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>

            {forgotPassword.isError && (
              <p role="alert" className="text-destructive text-sm font-medium">
                {getAuthErrorMessage(
                  forgotPassword.error,
                  "No pudimos enviar el enlace. Inténtalo de nuevo en unos minutos.",
                )}
              </p>
            )}

            <Button
              type="submit"
              size="lg"
              className="w-full"
              disabled={forgotPassword.isPending}
            >
              {forgotPassword.isPending ? (
                <span className="border-primary-foreground h-4 w-4 animate-spin rounded-full border-2 border-t-transparent" />
              ) : (
                "Enviar enlace"
              )}
            </Button>
          </form>

          <Link
            to="/login"
            className="text-muted-foreground hover:text-foreground mt-8 inline-flex items-center gap-1.5 text-sm font-semibold transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            Volver a iniciar sesión
          </Link>
        </>
      ) : (
        <>
          <div className="mb-8">
            <div className="bg-accent text-accent-foreground mb-5 flex h-14 w-14 items-center justify-center rounded-full">
              <CheckCircle2 className="h-8 w-8" />
            </div>
            <h1 className="text-3xl font-extrabold tracking-tight">
              Revisa tu correo
            </h1>
            <p className="text-muted-foreground text-caption mt-2">
              Si tu correo está registrado, te enviamos un enlace a{" "}
              <strong className="text-foreground">{email}</strong> para restablecer
              tu contraseña.
            </p>
          </div>

          {/* Los 60 minutos son los del broker de Laravel
              (`api/config/auth.php`, `passwords.users.expire`), que es el mismo
              número que imprime el correo. Antes acá decía 24 horas: la
              pantalla y el correo le prometían plazos distintos a la misma
              persona. */}
          <p className="text-muted-foreground text-caption">
            El enlace expira en 60 minutos. Si no lo recibes, revisa la carpeta de
            spam o intenta nuevamente.
          </p>

          <div className="mt-8 space-y-3">
            <Button
              variant="outline"
              size="lg"
              className="w-full"
              onClick={reiniciar}
            >
              Usar otro correo
            </Button>
            <Link
              to="/login"
              className="text-muted-foreground hover:text-foreground flex items-center justify-center gap-1.5 text-sm font-semibold transition-colors"
            >
              <ArrowLeft className="h-4 w-4" />
              Volver a iniciar sesión
            </Link>
          </div>
        </>
      )}
    </AuthLayout>
  );
}
